import { useEffect } from "react";

import { isSessionStale } from "./useIdleTimer";

// At most one enforced auto-reload per hour, so a reload that lands on a build
// still below the deployed version (deploy not yet propagated) can't loop.
// Keyed separately from chunkReload's 30s cooldown and versionGate's
// min-version guard, so the three reload paths never suppress each other's
// distinct triggers — each just rate-limits its own.
const RELOAD_GUARD_KEY = "update-enforcer-reload-attempted";
const RELOAD_COOLDOWN_MS = 60 * 60 * 1000; // 1 hour
const IDLE_BOUNDARY_MS = 15 * 60 * 1000; // 15 minutes
const IDLE_POLL_MS = 60 * 1000; // re-check the idle boundary each minute

const canAutoReload = () => {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) || 0);
    return Date.now() - last >= RELOAD_COOLDOWN_MS;
  } catch {
    return true; // sessionStorage unavailable — don't block the reload
  }
};

const markAutoReload = () => {
  try {
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
  } catch {
    // sessionStorage unavailable — proceed anyway.
  }
};

/**
 * Force a stale tab onto the new build at a **safe boundary**, once an update
 * is pending (see useVersionCheck). Reloads only when the reload won't
 * interrupt active work:
 *  - the tab is **hidden** (backgrounded / minimized) — invisible, highest-value;
 *  - the tab is visible but **idle ≥15 min** (no input, via the shared
 *    `lastActivityTimestamp`).
 *
 * Deliberately NOT on route change — a full reload mid-navigation is the one
 * disruptive boundary; the escalating prompt (UpdateAvailableNotification)
 * covers an actively-used tab instead.
 *
 * Cross-browser: uses only the Page Visibility API + the activity-timestamp
 * (standard DOM events / storage), never the Chromium-only Idle Detection API,
 * so it behaves identically on Chrome / Edge / Firefox.
 *
 * @param {boolean} updateAvailable - whether a newer build has been detected.
 */
export function useUpdateEnforcer(updateAvailable) {
  useEffect(() => {
    if (!updateAvailable) return;

    const reloadIfSafe = () => {
      if (!canAutoReload()) return;
      const hidden = document.visibilityState === "hidden";
      const idleWhileVisible =
        document.visibilityState === "visible" &&
        isSessionStale(IDLE_BOUNDARY_MS);
      if (hidden || idleWhileVisible) {
        markAutoReload();
        window.location.reload();
      }
    };

    // If the tab is already hidden when the update lands, reload immediately;
    // otherwise wait for it to be backgrounded or to go idle.
    reloadIfSafe();

    document.addEventListener("visibilitychange", reloadIfSafe);
    const interval = setInterval(reloadIfSafe, IDLE_POLL_MS);
    return () => {
      document.removeEventListener("visibilitychange", reloadIfSafe);
      clearInterval(interval);
    };
  }, [updateAvailable]);
}
